import { useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { CvSectionBlock, CustomColors, CvLayout } from "@/types/cvTemplate";
import { generateHtmlFromSections } from "@/services/cvGenerationService";
import { Candidate } from "@/types/candidate";
import { CandidateExperience, CandidateSkill } from "@/hooks/candidate/useCandidateExperiences";

// Sample data for preview
const SAMPLE_CANDIDATE: Candidate = {
	id: "preview",
	name: "Jane Smith",
	email: "jane.smith@example.com",
	phone: "+32 470 123 456",
	function: "Senior Product Manager",
	location: "Brussels, Belgium",
	linkedin: "linkedin.com/in/janesmith",
	skills: ["Product Strategy", "Agile", "Stakeholder Management", "Data Analysis", "UX Research"],
	ai_synthesis: "Seasoned product manager with 8+ years of experience leading cross-functional teams in fast-paced SaaS environments. Proven track record of shipping products that drive revenue growth and user engagement. Strong analytical mindset with a user-centric approach to problem solving.",
};

const SAMPLE_EXPERIENCES: CandidateExperience[] = [
	{
		id: "1",
		candidate_id: "preview",
		role: "Senior Product Manager",
		company: "TechCorp Solutions",
		industry: "SaaS",
		start_date: "2022-03-01",
		end_date: null,
		is_current: true,
	},
	{
		id: "2",
		candidate_id: "preview",
		role: "Product Manager",
		company: "InnovateCo",
		industry: "Fintech",
		start_date: "2019-06-01",
		end_date: "2022-02-01",
		is_current: false,
	},
	{
		id: "3",
		candidate_id: "preview",
		role: "Associate Product Manager",
		company: "StartupHub",
		industry: "E-commerce",
		start_date: "2017-01-01",
		end_date: "2019-05-01",
		is_current: false,
	},
];

const SAMPLE_SKILLS: CandidateSkill[] = [
	{ name: "Product Strategy" },
	{ name: "Agile / Scrum" },
	{ name: "Stakeholder Management" },
	{ name: "Data Analysis" },
	{ name: "UX Research" },
	{ name: "SQL" },
	{ name: "Roadmap Planning" },
	{ name: "A/B Testing" },
];

interface CvPreviewPanelProps {
	sections: CvSectionBlock[];
	colorScheme?: string;
	customColors?: CustomColors;
	layout?: CvLayout;
	organizationName?: string;
}

export function CvPreviewPanel({ sections, colorScheme, customColors, layout, organizationName }: CvPreviewPanelProps) {
	const html = useMemo(() => generateHtmlFromSections({
		sections,
		candidate: SAMPLE_CANDIDATE,
		experiences: SAMPLE_EXPERIENCES,
		skills: SAMPLE_SKILLS,
		organizationName: organizationName || "Acme Recruiting",
		colorScheme,
		customColors,
		layout,
	}), [sections, colorScheme, customColors, layout, organizationName]);

	return (
		<Card className="h-full overflow-hidden">
			<CardContent className="p-6 h-full overflow-auto">
				<div className="max-w-[600px] mx-auto bg-white p-8 shadow-sm border rounded">
					<div dangerouslySetInnerHTML={{ __html: html }} />
				</div>
				<div className="mt-4 text-center">
					<p className="text-xs text-muted-foreground italic">
						Preview uses sample data. Actual CV will use real candidate information.
					</p>
				</div>
			</CardContent>
		</Card>
	);
}
